import { Button, Collapse, Switch, } from 'antd';
// ANT Design
import React, { useEffect, useState } from 'react';
import VariableDrawer from './VariableDrawer';
import TableConfigDrawer from './TableConfigDrawer';
import { useTranslation } from 'react-i18next';
import { MASKS_TYPE, PANEL_LIGHT_BG, PRIMARY_DARK, getPrimaryDark } from '../../constants/constants';
import { useModalOpener } from '../../hooks';
import { useStore } from '../../store/store';
import { actionButtonBottomMargin, filterSameColor, iconButtonStyle } from '../../utils/styles';
import { NavbarButton } from '../Layout/NavbarButton';
import { NavbarListButton } from '../Layout/NavbarListButton';
import { BoxLockManager } from '../Layout/BoxLockManager';
import { BoxRotationInterface } from '../common/BoxRotationInterface';
import ConfigurationModal from './configurationModal/ConfigurationModal';
import './RightSide.less';
import OverlaySider from '../Layout/OverlaySider';
import { getContainerById, getColumnById, getMaskById } from '../../store/lookups';
import { containerHasOneRowBox, isASelectionBox, isSelectedBoxFreelyPlaced } from '../../utils/boxes';
import ContainerLayoutPanel from './ContainerLayoutPanel';
import BoxFormControlsPortal from './BoxFormControlsPortal';
import { getTopLevelContainer } from '../../utils/columns';
import SectionOriginPanel from './SectionOriginPanel';
import SectionContextPanel from './SectionContextPanel';
import { leftShownStatus, useOverlay } from '../../contexts/OverlayContext';
import VariableQuickEditPanel from './VariableQuickEditPanel';
import VariableLineStyleControl from './VariableLineStyleControl';
import DuotoneLightIcon from '../common/DuotoneLightIcon';
import KeepTooltip from '../common/KeepTooltip';
import { TooltipsContext } from '../../contexts/TooltipsContext';
import { isPageBackgroundMask } from '../../utils/pageBackground';
import { isWindows } from '../../utils/browser';

const menuItemStyle = {
  textAlign: 'center',
  margin: `${actionButtonBottomMargin}px 0`,
  display: 'flex',
  justifyContent: 'center',
}

// Container dont la disposition (colonnes / lignes) peut se régler depuis le
// panel, ou null.
const layoutEditableContainerId = ({ columns, rowContainers, boxes }, container) => {
  if (!container) return null;
  // Mode dessin : positionnement libre, pas de grille de colonnes à régler.
  if (container.columnsIds.some((cid) => getColumnById(columns, cid)?.drawMode)) return null;
  // Container issu d'une section liée (récursive ou non) : sa structure
  // appartient à la section source et est réécrasée à chaque injection. La
  // suppression est déjà interdite (cf. hooks.js), le panel de disposition
  // proposait pourtant colonnes/lignes — des réglages sans effet durable.
  if (container.from_linked_section) return null;
  // Box pleine largeur (titre, tableau, sommaire... cf. isAOneRowBox) : elle
  // n'occupe qu'une colonne seule, 2+ colonnes contrediraient cette règle
  // (imposée entre autres pour le découpage des tableaux dans le PDF).
  if (containerHasOneRowBox(container, columns, rowContainers, boxes)) return null;
  return container.id;
};

// Juste avant « Origine » (SectionOriginPanel, 99), toujours dernière.
const BOX_CONTAINER_LAYOUT_ORDER = 98;

// Disposition du container de la box sélectionnée, repliée en bas du panel de
// la box : sans elle, il fallait désélectionner la box en cliquant dans la
// marge du container pour y accéder. Repliée par défaut, pour ne pas mêler
// réglages de la box et de la structure (cf. layoutContainerId). La déplier
// ouvre directement la colonne de la box (focusLayoutColumn, comme le clic
// dans la marge).
const BoxContainerLayoutSection = () => {
  const boxColumnId = useStore(({ selectedBoxId, boxes }) =>
    selectedBoxId ? boxes.find((b) => b.id === selectedBoxId)?.columnId ?? null : null
  );
  const containerId = useStore((state) =>
    boxColumnId
      ? layoutEditableContainerId(
        state,
        getTopLevelContainer({
          columnId: boxColumnId,
          containers: state.containers,
          rowContainers: state.rowContainers,
        })
      )
      : null
  );
  const focusLayoutColumn = useStore(({ focusLayoutColumn }) => focusLayoutColumn);
  const [open, setOpen] = useState(false);
  if (!containerId) return null;

  return (
    // Même en-tête gris qu'« Actions » : réglage du container, pas de la box —
    // une teinte d'accent l'aurait confondu avec les sections de la box.
    <BoxFormControlsPortal title='Disposition' neutralTitle order={BOX_CONTAINER_LAYOUT_ORDER}>
      {/* Même dépliant que « Par angle » ou « Bibliothèque de formes ». */}
      <Collapse
        ghost
        size='small'
        activeKey={open ? ['layout'] : []}
        onChange={(keys) => {
          const nextOpen = keys.includes('layout');
          setOpen(nextOpen);
          if (nextOpen) focusLayoutColumn(boxColumnId);
        }}
        items={[{
          key: 'layout',
          label: 'Colonnes et lignes du bloc',
          children: <ContainerLayoutPanel containerId={containerId} />,
        }]}
      />
    </BoxFormControlsPortal>
  );
};

const selector = (state) => ({
  currentDocument: state.currentDocument,
  loadingData: state.loadingData,
  isAdmin: state?.user?.isAdmin,
  user: state?.user,
  gridConfiguration: state.gridConfiguration,
  setDisplayMargins: state.setDisplayMargins,
  displayMargins: state.displayMargins,
  handleMaskList: state.handleMaskList,
  maskEditing: state.maskEditing,
  setMaskEditing: state.setMaskEditing,
  landscape: state.landscape,
  setConfiguration: state.setConfiguration,
  configuration: state.configuration,
});

const RightSide = () => {
  const { t } = useTranslation();
  const {
    currentDocument,
    loadingData,
    isAdmin,
    displayMargins,
    setDisplayMargins,
    landscape,
    configuration,
  } = useStore(selector);
  // Colonne dessin actuellement affichée dans le panel : même logique de
  // priorité que isDrawBoxActive (box sélectionné, sinon container actif),
  // mais on garde la colonne entière pour pouvoir en éditer les propriétés
  // (ex. couleur de fond) plutôt qu'un simple booléen.
  const drawBoxColumn = useStore(({ selectedBoxId, boxes, columns, containers, activeContainerId }) => {
    const selectedBox = selectedBoxId ? boxes.find((b) => b.id === selectedBoxId) : null;
    if (selectedBox) {
      const column = getColumnById(columns, selectedBox.columnId);
      return column?.drawMode ? column : null;
    }
    // Fallback : container en drawMode sélectionné mais vide (pas encore de box dedans).
    const container = getContainerById(containers, activeContainerId);
    const columnId = container?.columnsIds.find((cid) => getColumnById(columns, cid)?.drawMode);
    return columnId ? getColumnById(columns, columnId) : null;
  })
  const isDrawBoxActive = !!drawBoxColumn
  // Box en attente d'un contenu (section, section variable, PDF, masque) : ce
  // n'est qu'un emplacement, pas encore un contenu. Régler la couleur de fond de
  // sa colonne dessin n'a aucun sens tant qu'elle est vide -> on masque ce
  // réglage, comme avancer/reculer/verrou le sont déjà (cf. isDrawModeBoxSelected).
  const isSelectionBoxSelected = useStore(({ selectedBoxId, boxes }) => {
    const selectedBox = selectedBoxId ? boxes.find((b) => b.id === selectedBoxId) : null;
    return isASelectionBox(selectedBox?.type);
  })
  const setColumnBackgroundColor = useStore(({ setColumnBackgroundColor }) => setColumnBackgroundColor)
  // Masque plein page en cours d'édition (page de garde, page de fin,
  // séparation de chapitres...) : l'éditeur de masque ne manipule aucune colonne
  // du document, le fond se règle donc sur le masque lui-même. Écarté quand
  // entête et pied de page sont édités ensemble : le réglage viserait deux
  // masques à la fois.
  const editedMask = useStore(({ maskEditing, masks }) =>
    maskEditing.length === 1 ? getMaskById(masks, maskEditing[0]) : null
  )
  const updateMask = useStore(({ updateMask }) => updateMask)
  // Le décor de page trace les marges par défaut : son réglage est distinct de
  // celui des box en mode dessin (displayMargins, masqué par défaut).
  const isEditingPageBackground = isPageBackgroundMask(editedMask)
  const hidePageBackgroundMargins = useStore(({ hidePageBackgroundMargins }) => hidePageBackgroundMargins)
  const togglePageBackgroundMargins = useStore(({ togglePageBackgroundMargins }) => togglePageBackgroundMargins)
  // Cible du réglage de fond : la colonne dessin sélectionnée, sinon le masque
  // en cours d'édition. Même liste de couleurs et même rendu dans les deux cas
  // (Column applique `backgroundColor` à toute colonne en mode dessin, et un
  // masque en est une).
  const backgroundTarget = drawBoxColumn
    ? {
      color: drawBoxColumn.backgroundColor,
      set: (color) => setColumnBackgroundColor(drawBoxColumn.id, color),
    }
    : editedMask
      ? {
        color: editedMask.backgroundColor,
        set: (color) => updateMask({ id: editedMask.id, fn: (mask) => { mask.backgroundColor = color; } }),
      }
      : null
  // Déplacer avant/arrière n'a de sens que pour une box dans un container
  // dessin (positionnement libre, boxes qui peuvent se superposer).
  const isDrawModeBoxSelected = useStore(isSelectedBoxFreelyPlaced)
  // Multi-sélection (ctrl/cmd + clic) : avancer/reculer, verrou et rotation ne
  // toucheraient que la box maîtresse. Comme les sections de réglages du panel,
  // ils s'effacent et il ne reste que les actions, qui valent pour toute la
  // sélection.
  const isMultiSelection = useStore(({ groupSelection }) => groupSelection.length > 0)
  // Réglages de disposition (colonnes / lignes) du container sélectionné.
  // Uniquement quand aucune box n'est sélectionnée : sinon le panel cumulerait
  // les réglages de la box et ceux de la structure, illisible. Sélectionner une
  // variable texte custom (handleBoxVariableSelectedId) met selectedBoxId à
  // null en template — le guard seul ne suffit donc plus, il faut l'exclure
  // aussi explicitement, sinon la disposition s'affiche sous l'édition rapide.
  // Une box sélectionnée a sa propre entrée : BoxContainerLayoutSection, repliée
  // dans le panel de la box.
  const layoutContainerId = useStore((state) => {
    const { selectedBoxId, boxVariableSelectedId, variables, containers, activeContainerId } = state;
    if (selectedBoxId) return null;
    if (boxVariableSelectedId && variables[boxVariableSelectedId]) return null;
    return layoutEditableContainerId(state, getContainerById(containers, activeContainerId));
  })
  const selectedBoxId = useStore(({ selectedBoxId }) => selectedBoxId)
  const moveBoxLayer = useStore(({ moveBoxLayer }) => moveBoxLayer)
  const { overlayProps } = useOverlay();
  const boxVariableSelectedId = useStore(({ boxVariableSelectedId }) => boxVariableSelectedId);
  // Booléen dérivé (et non l'objet variables entier) pour ne re-render que si
  // le statut "sélection = variable texte custom" change, pas à chaque frappe
  // dans une variable quelconque du document.
  const isCustomTextVariableSelected = useStore(({ variables }) => !!boxVariableSelectedId && !!variables[boxVariableSelectedId]);
  // Édition rapide : uniquement si la sélection est une variable texte custom
  // (pas une box-variable standard), et masquée si le formulaire plein écran
  // gauche est déjà ouvert sur cette même variable
  const showVariableQuickEditPanel = currentDocument.type === 'template' &&
    isCustomTextVariableSelected &&
    overlayProps.left.shownStatus !== leftShownStatus.VARIABLES;
  // Entête + drawer d'édition rapide : uniquement quand une VARIABLE TEXTE
  // custom est réellement sélectionnée (présente dans le dictionnaire variables).
  // Une box texte simple pose boxVariableSelectedId = son propre id (pour l'insert
  // @-variable), ce qui affichait à tort « Variable sélectionnée » sur une box
  // texte vide sans variable. On se cale donc sur isCustomTextVariableSelected,
  // en template et hors formulaire plein écran.
  const canQuickEditVariable = currentDocument.type === 'template' &&
    isCustomTextVariableSelected &&
    overlayProps.left.shownStatus !== leftShownStatus.VARIABLES;
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    if (!canQuickEditVariable) setDrawerOpen(false);
  }, [canQuickEditVariable]);
  // Même drawer pour une case à cocher / un choix : il y montre les réglages de
  // formulaire que la section du panel laisse de côté (réponse unique, nombres
  // min/max, remarque, valeur par défaut, valeurs conditionnelles). Ouvert par
  // la section elle-même, donc via le store (cf. openVariableFormDrawer).
  const variableFormDrawerBoxId = useStore(({ variableFormDrawerBoxId }) => variableFormDrawerBoxId);
  const closeVariableFormDrawer = useStore(({ closeVariableFormDrawer }) => closeVariableFormDrawer);
  // Fermeture sur une autre box sélectionnée seulement, pas sur une
  // désélection : éditer une valeur conditionnelle dans le drawer désélectionne
  // la box (cf. TriggerResultPopover), ce qui refermait le drawer au clic sur
  // le crayon. La box supprimée est gérée par le drawer lui-même.
  useEffect(() => {
    if (variableFormDrawerBoxId && selectedBoxId && selectedBoxId !== variableFormDrawerBoxId) {
      closeVariableFormDrawer();
    }
  }, [selectedBoxId, variableFormDrawerBoxId, closeVariableFormDrawer]);
  const [configurationPanelVisible, setConfigurationPanelVisible] =
    useModalOpener(false);

  const { setMaskEditing } = useStore(selector);

  return (
    <>
      {isAdmin && !loadingData && configurationPanelVisible && (
        <ConfigurationModal
          open={true}
          handleOk={() => setConfigurationPanelVisible(false)}
        />
      )}
      {currentDocument.type === 'template' && (
        <VariableDrawer
          open={(drawerOpen && canQuickEditVariable) || !!variableFormDrawerBoxId}
          onClose={() => {
            setDrawerOpen(false);
            closeVariableFormDrawer();
          }}
          boxId={variableFormDrawerBoxId ?? boxVariableSelectedId}
        />
      )}
      <TableConfigDrawer />
      <OverlaySider
        side={'right'}
      >
        {/* Aucune infobulle dans le panneau (cf. TooltipsContext) ; les contrôles
            à garder sont enveloppés dans KeepTooltip. */}
        <TooltipsContext.Provider value={false}>
        <div className='logo' />

        <div style={{ backgroundColor: PANEL_LIGHT_BG }} id='global-menu'>
          <div className={isWindows() ? 'menu_wrapper windows-scrollbar' : 'menu_wrapper'} style={{
            height: 'calc(100vh - 54px)',
            overflow: 'hidden scroll'
          }}>
            <div className='menu_item-main_block'>
              <div style={{
                position: 'sticky',
                top: 0,
                backgroundColor: PANEL_LIGHT_BG,
                zIndex: 999,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div>
                  {canQuickEditVariable && (
                    <div
                      key='variable-quick-edit-header'
                      style={{ ...menuItemStyle, flexDirection: 'column', alignItems: 'flex-start', padding: '0 8px', gap: 4 }}
                    >
                      <span style={{ fontWeight: 700, color: PRIMARY_DARK }}>Variable sélectionnée</span>
                      <span
                        onClick={() => setDrawerOpen((o) => !o)}
                        style={{ cursor: 'pointer', color: PRIMARY_DARK, fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <DuotoneLightIcon name='chevron-left' style={{ fontSize: 11 }} />
                        Voir plus de détails
                      </span>
                    </div>
                  )}
                  {!(currentDocument.type === 'template' && isAdmin) && !landscape && (
                    <div key='header-footer-editor' style={menuItemStyle}>
                      <Button
                        className='style_btn_right_side'
                        icon={<DuotoneLightIcon name='memo-pad' style={{ marginRight: "8px" }} />}
                        ghost
                        style={{ color: PRIMARY_DARK, borderColor: PRIMARY_DARK }}
                        onClick={() => setMaskEditing([MASKS_TYPE.HEADER.id, MASKS_TYPE.FOOTER.id])}
                      >
                        {t('header-footer')}
                      </Button>
                    </div>
                  )}
                  {showVariableQuickEditPanel && (
                    <div style={{ paddingTop: 4 }}>
                      <VariableQuickEditPanel variableId={boxVariableSelectedId} />
                    </div>
                  )}
                  {/* Mode proposition : pas d'édition rapide de la variable, mais
                      le nombre de lignes d'une adresse se règle sur place. Le
                      contrôle ne s'affiche que pour une occurrence éligible. */}
                  {currentDocument.type !== 'template' && <VariableLineStyleControl framed />}
                  {backgroundTarget && (
                    // data-outside-ignore : container dessin vide, rien d'autre
                    // que lui n'est sélectionné. Le mousedown sur l'interrupteur
                    // le désélectionnait (handleClickOutside de Container.jsx) et
                    // démontait ce bloc avant le click : les marges ne
                    // s'affichaient jamais.
                    <div
                      key='grille'
                      data-outside-ignore="true"
                      style={{ ...menuItemStyle, flexDirection: 'column', alignItems: 'center', gap: 8 }}
                    >
                      {(isDrawBoxActive || isEditingPageBackground) && (
                        <Switch
                          className='right-panel-switch'
                          checkedChildren={t('hide-margins')}
                          unCheckedChildren={t('show-margins')}
                          checked={isEditingPageBackground ? !hidePageBackgroundMargins : displayMargins}
                          onClick={() => {
                            if (isEditingPageBackground) togglePageBackgroundMargins();
                            else setDisplayMargins();
                          }}
                        />
                      )}
                      {/* Label visible plutôt qu'un tooltip : le bouton seul (icône
                          "goutte" sans texte) ne disait pas ce qu'il faisait.
                          Pas de fond sur le décor de page : il est posé par-dessus
                          le contenu, un fond opaque le recouvrirait en entier. Et
                          l'absence de couleur s'affichait comme un blanc coché,
                          qu'on prenait pour « transparent ». */}
                      {!isSelectionBoxSelected && !isEditingPageBackground && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span>{t('background-color')}</span>
                          <NavbarListButton
                            key='drawBoxBgColor'
                            colorInIcon
                            icon={<DuotoneLightIcon name='fill-drip' style={iconButtonStyle()} />}
                            onListElementClick={(newColor) => {
                              const colorStr = configuration.colors.find(
                                (color) => color.label === newColor
                              )?.style;
                              if (colorStr) backgroundTarget.set(colorStr);
                            }}
                            listElements={filterSameColor(configuration.colors)}
                            // Sans couleur de fond (absente ou transparente, le défaut
                            // d'une colonne), le fond est celui de la page : blanc.
                            selectedElement={configuration.colors.find(
                              (color) => !backgroundTarget.color || backgroundTarget.color === 'transparent'
                                ? color.label === 'white'
                                : color.style === backgroundTarget.color
                            )}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {/* Se rend par portail dans RightSideQuickEditPortal (plus
                      bas), en dernière section : monté ici pour être présent
                      même sans box sélectionnée — une section liée bloque la
                      sélection de ses box, seul le container est actif. */}
                  <SectionOriginPanel />
                  {/* Même portail, section précédente : contexte de section en
                      mode proposition (fil d'Ariane + occurrence). Monté ici
                      pour la même raison — il doit répondre au clic sur un
                      container, pas seulement sur une box. */}
                  <SectionContextPanel />
                  {/* key : chaque box repart repliée. */}
                  <BoxContainerLayoutSection key={selectedBoxId ?? 'none'} />
                  {layoutContainerId && (
                    <ContainerLayoutPanel containerId={layoutContainerId} />
                  )}
                  {isDrawModeBoxSelected && !isMultiSelection && (
                    <div key='draw-box-tools' style={{ ...menuItemStyle, gap: 8 }}>
                      <KeepTooltip>
                      <NavbarButton
                        tooltipContent={t('move-front')}
                        onClick={() => moveBoxLayer(selectedBoxId, 1)}
                        icon={<DuotoneLightIcon name='layer-plus' style={iconButtonStyle()} />}
                      />
                      <NavbarButton
                        tooltipContent={t('move-back')}
                        onClick={() => moveBoxLayer(selectedBoxId, -1)}
                        icon={<DuotoneLightIcon name='layer-minus' style={iconButtonStyle()} />}
                      />
                      </KeepTooltip>
                      <BoxLockManager />
                      <BoxRotationInterface />
                    </div>
                  )}
                </div>
              </div>
              {/* --panel-accent : couleur du thème mode-aware (navy proposal /
                  violet template), consommée par RightSide.less pour teinter les
                  icônes/switches du panel comme le rail gauche. */}
              <div
                id='RightSideQuickEditPortal'
                style={{ display: 'flex', flexDirection: 'column', '--panel-accent': getPrimaryDark(currentDocument?.type === 'template') }}
              />
            </div>
          </div>
        </div>
        </TooltipsContext.Provider>
      </OverlaySider >
    </>
  );
};

export default React.memo(RightSide);
